import { useState, useEffect } from 'react';
import { publicAPI } from '../services/api';
import { mockData } from '../data/mock';

// Custom hook to fetch data with fallback to mock
export const useWebsiteContent = () => {
  const [data, setData] = useState({
    hero: mockData.hero,
    courses: mockData.courses,
    teachers: mockData.teachers,
    testimonials: mockData.testimonials,
    faq: mockData.faq,
    categories: mockData.categories,
    values: mockData.values,
    courseTypes: mockData.courseTypes,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usingMock, setUsingMock] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        // Fetch all content in parallel
        const [
          heroRes,
          coursesRes,
          teachersRes,
          testimonialsRes,
          faqsRes,
          categoriesRes,
          valuesRes,
          courseTypesRes,
        ] = await Promise.all([
          publicAPI.getHero().catch(() => null),
          publicAPI.getCourses().catch(() => null),
          publicAPI.getTeachers().catch(() => null),
          publicAPI.getTestimonials().catch(() => null),
          publicAPI.getFaqs().catch(() => null),
          publicAPI.getCategories().catch(() => null),
          publicAPI.getValues().catch(() => null),
          publicAPI.getCourseTypes().catch(() => null),
        ]);

        const newData = { ...mockData };
        let usedAnyApiData = false;

        // Update with API data if available
        if (heroRes?.data && Object.keys(heroRes.data).length > 0) {
          newData.hero = heroRes.data;
          usedAnyApiData = true;
        }
        
        if (coursesRes?.data?.courses?.length > 0) {
          newData.courses = coursesRes.data.courses;
          usedAnyApiData = true;
        }
        
        if (teachersRes?.data?.teachers?.length > 0) {
          newData.teachers = teachersRes.data.teachers;
          usedAnyApiData = true;
        }
        
        if (testimonialsRes?.data?.testimonials?.length > 0) {
          newData.testimonials = testimonialsRes.data.testimonials;
          usedAnyApiData = true;
        }
        
        if (faqsRes?.data?.faqs?.length > 0) {
          newData.faq = faqsRes.data.faqs;
          usedAnyApiData = true;
        }
        
        if (categoriesRes?.data?.categories?.length > 0) {
          newData.categories = categoriesRes.data.categories;
          usedAnyApiData = true;
        }
        
        if (valuesRes?.data?.values?.length > 0) {
          newData.values = valuesRes.data.values;
          usedAnyApiData = true;
        }
        
        if (courseTypesRes?.data?.course_types?.length > 0) {
          newData.courseTypes = courseTypesRes.data.course_types;
          usedAnyApiData = true;
        }

        setData(newData);
        setUsingMock(!usedAnyApiData);
      } catch (err) {
        console.error('Error fetching website content:', err);
        setError('Failed to load content from server, using cached data');
        // Keep using mock data on error
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, []);

  return { data, loading, error, usingMock };
};

// Individual hooks for specific content types
export const useCourses = () => {
  const [courses, setCourses] = useState(mockData.courses);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    publicAPI.getCourses()
      .then(res => {
        if (res.data?.courses?.length > 0) {
          setCourses(res.data.courses);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  return { courses, loading };
};

export const useTeachers = () => {
  const [teachers, setTeachers] = useState(mockData.teachers);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    publicAPI.getTeachers()
      .then(res => {
        if (res.data?.teachers?.length > 0) {
          setTeachers(res.data.teachers);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  return { teachers, loading };
};

export const useTestimonials = () => {
  const [testimonials, setTestimonials] = useState(mockData.testimonials);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    publicAPI.getTestimonials()
      .then(res => {
        if (res.data?.testimonials?.length > 0) {
          setTestimonials(res.data.testimonials);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  return { testimonials, loading };
};

export const useFaqs = () => {
  const [faqs, setFaqs] = useState(mockData.faq);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    publicAPI.getFaqs()
      .then(res => {
        if (res.data?.faqs?.length > 0) {
          setFaqs(res.data.faqs);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  return { faqs, loading };
};

export default useWebsiteContent;
